import React, { useMemo, useState } from 'react';
import {
  Upload,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Download,
  CheckCircle2,
  XCircle,
  FileJson,
} from 'lucide-react';
import { BIOSDK_API_BASE_URL } from '../config';
import {
  SLAP_STEPS,
  runSlapLab,
  runSegmentOnly,
  downloadAllArtifacts,
  downloadManifest,
  templateExtension,
  signedBytesToUint8Array,
  base64ToUint8Array,
  downloadBlob,
  buildSegmentPreviewUrl,
  extractApiError,
} from '../slapFingerLab';
import SlapCompositePreview from './SlapCompositePreview';
import { API_VARIANT_MINEX, normalizeApiVariant } from '../biosdkRoutes';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';

export default function FingerSlapPanel({ apiVariant = 'mosip' }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [files, setFiles] = useState({ left: null, right: null, thumbs: null });
  const [skippedSteps, setSkippedSteps] = useState({ left: false, right: false, thumbs: false });
  const [previews, setPreviews] = useState({ left: null, right: null, thumbs: null });
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [segmentOnlyResults, setSegmentOnlyResults] = useState([]);
  const [labMode, setLabMode] = useState('full');
  const [imageSizes, setImageSizes] = useState({});
  const [enlargedUrl, setEnlargedUrl] = useState(null);

  const currentStep = SLAP_STEPS[stepIndex];
  const uploadedCount = SLAP_STEPS.filter((s) => files[s.id]).length;
  const canRunLab = uploadedCount > 0;
  const isMinex = normalizeApiVariant(apiVariant) === API_VARIANT_MINEX;
  const tplExt = useMemo(() => templateExtension(apiVariant), [apiVariant]);

  const clearStepMedia = (stepId) => {
    setFiles((prev) => ({ ...prev, [stepId]: null }));
    setPreviews((prev) => {
      if (prev[stepId]) URL.revokeObjectURL(prev[stepId]);
      return { ...prev, [stepId]: null };
    });
    setImageSizes((prev) => {
      const next = { ...prev };
      delete next[stepId];
      return next;
    });
  };

  const handleFileChange = (stepId, file) => {
    if (!file) return;
    setSkippedSteps((prev) => ({ ...prev, [stepId]: false }));
    setFiles((prev) => ({ ...prev, [stepId]: file }));
    const url = URL.createObjectURL(file);
    setPreviews((prev) => {
      if (prev[stepId]) URL.revokeObjectURL(prev[stepId]);
      return { ...prev, [stepId]: url };
    });
    const img = new Image();
    img.onload = () => {
      setImageSizes((prev) => ({
        ...prev,
        [stepId]: { width: img.naturalWidth, height: img.naturalHeight },
      }));
    };
    img.src = url;
    setResults([]);
    setSegmentOnlyResults([]);
    setError(null);
  };

  const handleSkipStep = (stepId) => {
    clearStepMedia(stepId);
    setSkippedSteps((prev) => ({ ...prev, [stepId]: true }));
    setResults([]);
    setSegmentOnlyResults([]);
    setError(null);
    if (stepIndex < SLAP_STEPS.length - 1) {
      setStepIndex((i) => i + 1);
    }
  };

  const handleUndoSkip = (stepId) => {
    setSkippedSteps((prev) => ({ ...prev, [stepId]: false }));
  };

  const handleRunSegmentOnly = async () => {
    if (!canRunLab) {
      setError('Upload at least one slap image (skip steps that do not apply).');
      return;
    }
    setProcessing(true);
    setError(null);
    setSegmentOnlyResults([]);
    setResults([]);
    try {
      const segResults = await runSegmentOnly(BIOSDK_API_BASE_URL, files, setProgress);
      setSegmentOnlyResults(segResults);
      setLabMode('segment');
    } catch (err) {
      setError(extractApiError(err) || err.message || 'Segmentation failed');
    } finally {
      setProcessing(false);
      setProgress(null);
    }
  };

  const handleRunLab = async () => {
    if (!canRunLab) {
      setError('Upload at least one slap image (skip steps that do not apply).');
      return;
    }
    setProcessing(true);
    setError(null);
    setResults([]);
    try {
      const labResults = await runSlapLab(BIOSDK_API_BASE_URL, files, apiVariant, setProgress);
      setResults(labResults);
      setLabMode('full');
    } catch (err) {
      setError(extractApiError(err) || 'Slap lab failed');
    } finally {
      setProcessing(false);
      setProgress(null);
    }
  };

  const downloadSegment = (row) => {
    if (!row.segment?.encodedImage) return;
    downloadBlob(`${row.bioSubType}.png`, base64ToUint8Array(row.segment.encodedImage));
  };

  const downloadTemplate = (row) => {
    if (!row.extractResult?.template) return;
    downloadBlob(`${row.bioSubType}.${tplExt}`, signedBytesToUint8Array(row.extractResult.template));
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground text-center">
        {isMinex
          ? 'Slap composites are segmented via biosdk-service, then each finger is extracted with MINEX (/minex/extract-template → ANSI 378).'
          : 'Slap composites are segmented via biosdk-service, then each finger is extracted using the selected API variant.'}
        {' '}Upload one or more slap images; skip any step that does not apply (e.g. missing NIST captures).
        No ABIS enrollment — preview and download results locally.
      </p>

      <div className="flex flex-wrap justify-center gap-2">
        {SLAP_STEPS.map((step, idx) => (
          <Badge
            key={step.id}
            variant={files[step.id] ? 'finger' : skippedSteps[step.id] ? 'secondary' : 'outline'}
            className={idx === stepIndex ? 'ring-2 ring-finger' : ''}
          >
            {idx + 1}. {step.shortLabel || step.label.split('(')[0].trim()}
            {files[step.id] ? ' ✓' : skippedSteps[step.id] ? ' — skipped' : ''}
          </Badge>
        ))}
      </div>

      <div className="rounded-lg border p-6 space-y-4 max-w-2xl mx-auto">
        <div className="text-center">
          <h3 className="text-lg font-semibold">{currentStep.label}</h3>
          <p className="text-sm text-muted-foreground mt-1">{currentStep.hint}</p>
        </div>

        <div className="upload-zone min-h-[180px] flex flex-col items-center justify-center">
          {skippedSteps[currentStep.id] && !files[currentStep.id] ? (
            <div className="text-center py-8 space-y-2">
              <p className="text-sm text-muted-foreground">This slap capture is skipped.</p>
              <Button type="button" variant="outline" size="sm" onClick={() => handleUndoSkip(currentStep.id)}>
                Undo skip — upload image
              </Button>
            </div>
          ) : (
            <>
              <input
                type="file"
                id={`slap-file-${currentStep.id}`}
                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                className="hidden"
                onChange={(e) => handleFileChange(currentStep.id, e.target.files?.[0])}
              />
              <label htmlFor={`slap-file-${currentStep.id}`} className="cursor-pointer text-center w-full">
                {previews[currentStep.id] ? (
                  <img
                    src={previews[currentStep.id]}
                    alt={currentStep.label}
                    className="mx-auto max-h-48 object-contain rounded border"
                  />
                ) : (
                  <>
                    <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload composite slap image</p>
                  </>
                )}
              </label>
              {files[currentStep.id] && (
                <p className="text-xs text-gray-500 mt-2 truncate max-w-full">{files[currentStep.id].name}</p>
              )}
            </>
          )}
        </div>

        <div className="flex flex-wrap justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={stepIndex === 0}
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex flex-wrap gap-2">
            {!files[currentStep.id] && !skippedSteps[currentStep.id] && (
              <Button type="button" variant="ghost" onClick={() => handleSkipStep(currentStep.id)}>
                Skip this step
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              disabled={stepIndex >= SLAP_STEPS.length - 1}
              onClick={() => setStepIndex((i) => Math.min(SLAP_STEPS.length - 1, i + 1))}
            >
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {uploadedCount > 0 && uploadedCount < SLAP_STEPS.length && (
        <p className="text-center text-xs text-muted-foreground">
          {uploadedCount} of {SLAP_STEPS.length} slap images uploaded — skipped steps will not be processed.
        </p>
      )}

      <div className="flex flex-wrap justify-center gap-3">
        <Button
          variant="outline"
          size="lg"
          disabled={!canRunLab || processing}
          onClick={handleRunSegmentOnly}
        >
          {processing && labMode === 'segment' ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Segmenting…
            </>
          ) : (
            'Segment only'
          )}
        </Button>
        <Button
          variant="finger"
          size="lg"
          disabled={!canRunLab || processing}
          onClick={handleRunLab}
        >
          {processing && labMode === 'full' ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {progress?.phase === 'extract'
                ? `Extracting ${progress.bioSubType || ''}…`
                : 'Segmenting…'}
            </>
          ) : (
            'Run slap lab (segment + extract)'
          )}
        </Button>
      </div>

      {segmentOnlyResults.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-center text-sm font-medium text-muted-foreground">
            Segment-only preview (bbox overlays — coordinate-free /segment-slap)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {segmentOnlyResults.map((row) => (
              <Card key={row.slapStepId} className="overflow-hidden">
                <CardContent className="p-3 space-y-2">
                  <div className="text-xs font-medium">{row.label}</div>
                  <SlapCompositePreview
                    imageUrl={row.compositeUrl}
                    imageSize={imageSizes[row.slapStepId]}
                    segments={row.segments}
                    onClick={() => setEnlargedUrl(row.compositeUrl)}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    {row.segments.map((seg) => (
                      <div key={seg.bioSubType} className="text-center">
                        <img
                          src={buildSegmentPreviewUrl(seg.encodedImage)}
                          alt={seg.bioSubType}
                          className="h-16 w-full object-contain bg-gray-50 rounded border"
                        />
                        <div className="text-[10px] truncate">{seg.bioSubType}</div>
                        <div className="text-[10px] text-muted-foreground">
                          conf {(seg.confidence ?? 0).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Lab failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {results.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 justify-center">
            <Button type="button" variant="outline" size="sm" onClick={() => downloadAllArtifacts(results, apiVariant, 'segments')}>
              <Download className="h-4 w-4 mr-1" /> All segment images
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => downloadAllArtifacts(results, apiVariant, 'templates')}>
              <Download className="h-4 w-4 mr-1" /> All templates
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => downloadAllArtifacts(results, apiVariant, 'all')}>
              <Download className="h-4 w-4 mr-1" /> Download everything
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => downloadManifest(results, apiVariant)}>
              <FileJson className="h-4 w-4 mr-1" /> Manifest JSON
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {results.map((row) => (
              <Card key={`${row.slapType}-${row.bioSubType}`} className="overflow-hidden">
                <CardContent className="p-3 space-y-2">
                  <button
                    type="button"
                    className="w-full"
                    onClick={() => setEnlargedUrl(row.previewUrl)}
                  >
                    {row.previewUrl ? (
                      <img
                        src={row.previewUrl}
                        alt={row.bioSubType}
                        className="w-full h-24 object-contain bg-gray-50 rounded border"
                      />
                    ) : (
                      <div className="h-24 bg-gray-100 rounded" />
                    )}
                  </button>
                  <div className="text-xs font-medium truncate">{row.bioSubType}</div>
                  <div className="text-xs text-muted-foreground">
                    conf {(row.segment?.confidence ?? 0).toFixed(2)}
                  </div>
                  {row.extractError ? (
                    <div className="flex items-center gap-1 text-xs text-red-600">
                      <XCircle className="h-3 w-3" /> {row.extractError}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-xs text-green-700">
                      <CheckCircle2 className="h-3 w-3" />
                      {row.extractResult?.templateLength ?? 0} B
                    </div>
                  )}
                  <div className="flex gap-1">
                    <Button type="button" variant="outline" size="sm" className="flex-1 text-xs px-1" onClick={() => downloadSegment(row)}>
                      PNG
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="flex-1 text-xs px-1"
                      disabled={!row.extractResult?.template}
                      onClick={() => downloadTemplate(row)}
                    >
                      {tplExt.toUpperCase()}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {enlargedUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setEnlargedUrl(null)}
          onKeyDown={(e) => e.key === 'Escape' && setEnlargedUrl(null)}
          role="button"
          tabIndex={0}
        >
          <img src={enlargedUrl} alt="Preview" className="max-h-[90vh] max-w-full object-contain" />
        </div>
      )}
    </div>
  );
}
